import Image from '../../../components/Image';
import { Mixpanel } from '../../../services/Mixpanel';
import { cleanBookTitle, getChapterFromIdx } from '../../Homepage/Contexts/DiffitBooksContext';
import { ChapterDropdown } from './ChapterDropdown';

/**
 * Detail view for a selected book, allowing the user to pick a start and end chapter range.
 * @param {Object} props
 * @param {Object} props.selectedBook - The book object currently selected.
 * @param {number} props.startChapterIdx - Index of the selected start chapter (-1 if none).
 * @param {Function} props.setStartChapterIdx - Setter for the start chapter index.
 * @param {number} props.endChapterIdx - Index of the selected end chapter (-1 if none).
 * @param {Function} props.setEndChapterIdx - Setter for the end chapter index.
 * @param {Array} props.startChapterOptions - Available options for the start chapter dropdown.
 * @param {Array} props.endChapterOptions - Available options for the end chapter dropdown.
 * @param {boolean} props.canConfirm - Whether the confirm button should be enabled.
 * @param {Function} props.onConfirm - Callback invoked when the user confirms the selection.
 * @param {Function} props.onCancel - Callback invoked when the user cancels.
 */
export const DetailView = ({
  selectedBook,
  startChapterIdx,
  setStartChapterIdx,
  endChapterIdx,
  setEndChapterIdx,
  startChapterOptions,
  endChapterOptions,
  canConfirm,
  onConfirm,
  onCancel,
}) => {
  return (
    <>
      <div className='px-6 pt-10 pb-10 overflow-y-auto flex-1'>
        <div className='max-w-md mx-auto'>
          {/* Book Info */}
          <div className='flex items-center gap-5 mb-6'>
            <div className='w-32 flex-shrink-0'>
              <Image
                src={selectedBook.cover}
                alt={cleanBookTitle(selectedBook.title)}
                className='w-full object-contain rounded-lg shadow-md'
              />
            </div>
            <div className='flex-1 min-w-0'>
              <h3 className='text-lg font-semibold text-gray-900 mb-0.5'>
                {cleanBookTitle(selectedBook.title)}
              </h3>
              <p className='text-gray-500 text-sm'>by {selectedBook.author}</p>
              <p className='text-xs text-gray-500 mt-2'>
                {selectedBook.chapters?.length || 0} chapters
              </p>
            </div>
          </div>

          {/* Chapter Selection */}
          <div className='space-y-5 mb-4'>
            <p className='text-base font-medium text-gray-700'>Select Chapter Range:</p>
            {/* Start Chapter */}
            <div>
              <label className='block text-sm text-gray-600 mb-1'>Start Chapter</label>
              <ChapterDropdown
                options={startChapterOptions}
                selected={getChapterFromIdx(selectedBook, startChapterIdx)}
                placeholder='Select chapter'
                onChange={(selected) => {
                  const newIdx = selected.value - 1;
                  setStartChapterIdx(newIdx);
                  if (endChapterIdx === -1) {
                    setEndChapterIdx(newIdx);
                  }
                  Mixpanel.track('Unlimited Book Modal Selected Start Chapter', {
                    chapter: newIdx,
                  });
                }}
              />
            </div>

            {/* End Chapter */}
            <div>
              <label className='block text-sm text-gray-600 mb-1'>End Chapter</label>
              <ChapterDropdown
                options={endChapterOptions}
                selected={getChapterFromIdx(selectedBook, endChapterIdx)}
                placeholder='Select chapter'
                onChange={(selected) => {
                  const newIdx = selected.value - 1;
                  setEndChapterIdx(newIdx);
                  Mixpanel.track('Unlimited Book Modal Selected End Chapter', {
                    chapter: newIdx,
                  });
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className='px-6 py-4 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0'>
        <button
          onClick={onCancel}
          className='px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors'>
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={!canConfirm}
          className='px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-200 disabled:cursor-not-allowed transition-colors'>
          Add Book
        </button>
      </div>
    </>
  );
};
